import { Component, ViewChild, ElementRef, ChangeDetectorRef, OnInit, OnDestroy, OnChanges, SimpleChanges, input, output, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { ModalModule } from "ngx-bootstrap/modal";
import { UtilisateurService } from "src/app/services/utilisateur.service";
import { Dossier } from "src/app/bo/dossier";
import { WorkflowEtapeInstance } from "src/app/bo/workflowEtapeInstance";
import { FonctionsService } from "src/app/services/fonctions-partagees.service";
import { HttpService } from "src/app/services/http.service";
import { HttpErrorResponse, HttpEventType } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { Fichier, DirectoryType } from "src/app/bo/fichier";
import { ModalDirective } from "ngx-bootstrap/modal";
import { forkJoin, map, Subscription } from "rxjs";
import { DocumentStorageService } from "src/app/services/document-storage.service";
import { KeycloakUtilisateurService } from "src/app/services/keycloak/keycloak-utilisateur.service";

@Component({
  selector: 'app-document-storage',
  templateUrl: './document-storage.component.html',
  styleUrls: ['./document-storage.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ModalModule
  ]
})
export class DocumentStorageComponent implements OnInit, OnDestroy, OnChanges {
  readonly tirId = input<number>();
  readonly racine = input<Dossier>();
  readonly apiEndPoint = input<string>("");
  readonly securityIsChange = output<WorkflowEtapeInstance>();
  @ViewChild('modalFormDossier') modalFormDossier: ModalDirective;
  @ViewChild('modalConfirmSuppr') modalConfirmSuppr: ModalDirective;
  @ViewChild('modalRenommer') modalRenommer: ModalDirective;
  @ViewChild('modalConfirmUpload') modalConfirmUpload: ModalDirective;
  @ViewChild('modalUpload') modalUpload: ModalDirective;
  @ViewChild('modalDownload') modalDownload: ModalDirective;
  @ViewChild('modalFichiersSupprimes') modalFichiersSupprimes: ModalDirective;
  @ViewChild('modalErreurSynchro') modalErreurSynchro: ModalDirective;
  @ViewChild("fileInput") fileInput: ElementRef;
  
  public expandedCards = signal<boolean[]>([]);
  public stepFolders = signal<Map<number, Dossier>>(new Map());
  public expandedSubFolders = signal<Map<string, boolean>>(new Map());
  public selectedItems = signal<Map<number, Set<string>>>(new Map());
  public stepDragOver = signal<Map<number, boolean>>(new Map());
  private readonly stepDragCounter = signal<Map<number, number>>(new Map());
  public subFolderDragOver = signal<Map<number, boolean>>(new Map());
  private readonly subFolderDragCounter = signal<Map<number, number>>(new Map());
  
  private currentRenamingFile = signal<Fichier | null>(null);
  private currentRenamingFolder = signal<Dossier | null>(null);
  private currentDeletingFile = signal<Fichier | null>(null);
  private filesRefreshSubscription: Subscription;
  private currentDeletingFolder = signal<Dossier | null>(null);
  private currentStepId = signal<number | null>(null);
  
  public currentFolder = signal<Dossier | null>(null);
  public downloadInProgress = signal(false);
  
  folderNameError = signal(false);
  folderNameRequired = signal(false);
  renameNameError = signal(false);
  renameNameRequired = signal(false);
  folderNameExists = signal(false);
  totalWeight = signal(0);
  
  public files = signal<File[]>([]);
  public totalSize = signal(0);
  public warningDuplicateFile = signal(false);
  public newName = signal('');
  public selectedFolderId = signal<number | null>(null);
  public isDragOver = signal(false);
  public selectedFiles = signal<Set<number>>(new Set());
  public fileDestinations = signal<(number | null)[]>([]);
  
  public isRenamingError = signal(false);
  progress = signal('0%');
  barProgress = signal(0);
  
  public canEditPermission = false;
  private updatingPermission = signal<Set<number>>(new Set());

  constructor(public utilService: UtilisateurService, private readonly fonctionsService: FonctionsService,
    private readonly http: HttpService, private readonly translate: TranslateService, private readonly cdRef: ChangeDetectorRef,
    private readonly documentStorageService: DocumentStorageService,
    private readonly keycloakUserService: KeycloakUtilisateurService
  ) {
    this.canEditPermission = this.keycloakUserService.hasRole('manage_site');
  }

  getProgressValue(): number {
    const numericValue = parseInt(this.progress().replace('%', ''), 10);
    return isNaN(numericValue) ? 0 : numericValue;
  }
  ngOnChanges(changes: SimpleChanges): void {
      if (changes['tirId']) {
        this.refreshData();
      }
    }
  ngOnInit() {
    this.loadAllStepFolders();
    this.filesRefreshSubscription = this.utilService.getFilesRefreshEvent().subscribe(() => {
      this.refreshData();
    });
    this.preventDefaultDragBehavior();
  }

  ngOnDestroy() {
    if (this.filesRefreshSubscription) {
      this.filesRefreshSubscription.unsubscribe();
    }
    this.removeDefaultDragBehavior();
  }
  
  private preventDefaultDragBehavior(): void {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      document.addEventListener(eventName, this.preventDefaults, false);
    });
  }
  
  private removeDefaultDragBehavior(): void {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      document.removeEventListener(eventName, this.preventDefaults, false);
    });
  }
  
  private readonly preventDefaults = (e: Event) => {
    const target = e.target as Element;
    const isDragZone = target.closest('.step-drag-zone');
    
    if (!isDragZone) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
  refreshData(){
    this.stepFolders.set(new Map());
    this.loadAllStepFolders();
  }

  private loadAllStepFolders() {
    if (!this.utilService.currentTir?.workflowInstance?.workflowEtapeInstances) {
      return;
    }

    this.utilService.currentTir.workflowInstance.workflowEtapeInstances.forEach(step => {
      if (step && step.workflowEtapeInstanceId && !this.stepFolders().has(step.workflowEtapeInstanceId)) {
        this.http.get<Dossier>('WorkflowSteps/' + step.workflowEtapeInstanceId + '/Folder').subscribe(
          folder => {
            if (folder) {
              this.stepFolders.update(m => new Map(m).set(step.workflowEtapeInstanceId, folder));
              this.cdRef.detectChanges();
            }
          },
          (error: HttpErrorResponse) => {
            console.warn(`Unable to load folder for step ${step.nom}:`, error);
          }
        );
      }
    });
  }

  addFolder(e: any) {
    this.folderNameError.set(false);
    this.folderNameRequired.set(false);
    this.folderNameExists.set(this.currentFolder()!.sousDossiers.find(o => o.nom == e.target[0].value.trim()) !== undefined);

    if (!this.validateFileOrFolderName(e.target[0].value, 'folderNameError', 'folderNameRequired') || this.folderNameExists())
      return false;

    this.fonctionsService.loading_screen(true);
    var newFolder = new Dossier();
    newFolder.dossierParentId = this.currentFolder()!.dossierId;
    newFolder.nom = e.target[0].value;
    
    
    this.http.post<Dossier, Dossier>(this.apiEndPoint() + '/' + this.currentStepId() + '/Folders/Add', newFolder).subscribe((res: Dossier) => {
      this.refreshCurrentFolder(res);
      
      if (this.currentStepId()) {
        this.stepFolders.update(m => new Map(m).set(this.currentStepId()!, res));
      }
      
      e.target[0].value = "";
      this.modalFormDossier.hide();
      this.currentStepId.set(null);
    }, () => { },
      () => this.fonctionsService.loading_screen(false));
  }

  validateFileOrFolderName(value: string, errorVar: string, requiredVar: string) {
    let forbiddenChars = ['/', '\\', ':', '*', '?', '"', '<', '>', '|', '.'];

    this[requiredVar].set(value == null || value.trim() == '');

    for (var i = 0; i < value.length; i++) {
      let char = value[i];
      if (forbiddenChars.find(o => o == char) != undefined)
        this[errorVar].set(true);
    }
    return !this[requiredVar]() && !this[errorVar]();
  }
  
  refreshCurrentFolder(d: Dossier) {
    if (this.currentFolder() && d.dossierId == this.currentFolder()?.dossierId) {
      this.currentFolder.set(d);
      this.totalWeight.set(this.calculateFolderSize(this.currentFolder()!));
    }
    else {
      d.sousDossiers.forEach(_d => {
        this.refreshCurrentFolder(_d);
      });
    }
  }
     
  calculateFolderSize(folder: Dossier): number {
    let size = 0;

    folder.fichiers.forEach(file => size += file.taille);
    folder.sousDossiers.forEach(subFolder => {
      size += this.calculateFolderSize(subFolder);
    });

    return size;
  }
  
  toggleCard(index: number, step: WorkflowEtapeInstance): void {
    const wasExpanded = this.expandedCards()[index];
    this.expandedCards.update(arr => { arr[index] = !arr[index]; return [...arr]; });
    
    if (this.expandedCards()[index] && !wasExpanded) {
      if (step && !this.stepFolders().has(step.workflowEtapeInstanceId)) {
        this.loadFolder(step);
      }
    }
    
    if (!this.selectedItems().has(step.workflowEtapeInstanceId)) {
      this.selectedItems.update(m => new Map(m).set(step.workflowEtapeInstanceId, new Set()));
    }

    const stepSelections = this.selectedItems().get(step.workflowEtapeInstanceId)!;
    const folder = this.getFolderForStep(step.workflowEtapeInstanceId);
    this.selectAllItems(folder, stepSelections);

  }

  getTotalFilesForStep(step: WorkflowEtapeInstance): number {
    const folder = this.getFolderForStep(step.workflowEtapeInstanceId);
    
    if (!folder) {
      return 0;
    }
    
    return this.calculateTotalFilesInFolder(folder);
  }
  
  private calculateTotalFilesInFolder(folder: Dossier): number {
    let fileCount = 0;

    if (folder.fichiers) {
      fileCount += folder.fichiers.length;
    }
    
    if (folder.sousDossiers) {
      folder.sousDossiers.forEach(subFolder => {
        fileCount += this.calculateTotalFilesInFolder(subFolder);
      });
    }

    return fileCount;
  }
  
  toggleSubFolder(folderId: number): void {
    const key = folderId.toString();
    this.expandedSubFolders.update(m => { const current = m.get(key); return new Map(m).set(key, !current); });
  }
  
  isSubFolderExpanded(folderId: number): boolean {
    return this.expandedSubFolders().get(folderId.toString()) || false;
  }
  
  getFolderForStep(stepId: number): Dossier | null {
    return this.stepFolders().get(stepId) || null;
  }
  
  getStepTitle(stepName: string): string {
    if (!stepName) return '';
    return stepName.replace(/^\d+-/, '').trim();
  }

  getAccessPermission(step: WorkflowEtapeInstance): string {
    return step.accessPermission === 1 ? 'Read' : step.accessPermission === 2 ? 'Read - Write' : 'None';
  }

  isPermissionUpdating(stepId: number): boolean {
    return this.updatingPermission().has(stepId);
  }

  onPermissionChange(etape: WorkflowEtapeInstance, newValue: number): void {
    if (!this.canEditPermission) {
      return;
    }

    if (this.updatingPermission().has(etape.workflowEtapeInstanceId)) {
      return;
    }

    const previousValue = etape.accessPermission;
    etape.accessPermission = +newValue;

    this.updatingPermission.update(s => new Set([...s, etape.workflowEtapeInstanceId]));

    this.http.put(this.apiEndPoint() + '/' + etape.workflowEtapeInstanceId + '/UpdateWorkflowStepInstance', +newValue)
      .subscribe({
        next: () => {
          this.updatingPermission.update(s => {
            const updated = new Set(s);
            updated.delete(etape.workflowEtapeInstanceId);
            return updated;
          });
          this.fonctionsService.launch_toast(true);
          this.securityIsChange.emit(etape);
        },
        error: (error: HttpErrorResponse) => {
          this.updatingPermission.update(s => {
            const updated = new Set(s);
            updated.delete(etape.workflowEtapeInstanceId);
            return updated;
          });
          etape.accessPermission = previousValue;
          this.fonctionsService.launch_toast(false);
        }
      });
  }

  exportStep(step: WorkflowEtapeInstance, event: Event): void {
    event.stopPropagation();
    
    if (!this.hasSelectedItems(step.workflowEtapeInstanceId)) {
      this.fonctionsService.launch_toast(false);
      return;
    }

    const folder = this.getFolderForStep(step.workflowEtapeInstanceId);
    if (folder) {
      const selectedData = this.buildSelectedItemsArrays(step.workflowEtapeInstanceId, folder);
      this.download(selectedData.selectedFiles, selectedData.selectedFolders, folder.nom, step.workflowEtapeInstanceId);
    } else {
      this.http.get<Dossier>('WorkflowSteps/' + step.workflowEtapeInstanceId + '/Folder').subscribe(folder => {
        if (folder) {
          this.stepFolders.update(m => new Map(m).set(step.workflowEtapeInstanceId, folder));
          const selectedData = this.buildSelectedItemsArrays(step.workflowEtapeInstanceId, folder);
          this.download(selectedData.selectedFiles, selectedData.selectedFolders, folder.nom, step.workflowEtapeInstanceId);
        }
      }, (error: HttpErrorResponse) => {
        console.error('Error loading folder for export:', error);
        this.fonctionsService.launch_toast(false);
      });
    }
  }

 exportAllDocuments(): void {
    if (!this.utilService.currentTir?.workflowInstance?.workflowEtapeInstances || 
        this.utilService.currentTir.workflowInstance.workflowEtapeInstances.length === 0) {
      this.fonctionsService.launch_toast(false);
      return;
    }

    let startLoader = setTimeout(() => {
      this.fonctionsService.loading_screen(true);
    }, 300);

    const stepFolders: Dossier[] = [];
    const loadPromises: Promise<void>[] = [];

    this.utilService.currentTir.workflowInstance.workflowEtapeInstances.forEach(step => {
      const promise = new Promise<void>((resolve, reject) => {
        const folder = this.getFolderForStep(step.workflowEtapeInstanceId);
        folder.stepId = step.workflowEtapeInstanceId;
        if (folder) {
          stepFolders.push(folder);
          resolve();
        } else {
          this.documentStorageService.getWorkflowStepFolder(step.workflowEtapeInstanceId).subscribe(
            folderLoaded => {
              if (folderLoaded) {
                this.stepFolders.update(m => new Map(m).set(step.workflowEtapeInstanceId, folderLoaded));
                stepFolders.push(folderLoaded);
                resolve();
              } else {
                reject(`Unable to load folder for step ${step.nom}`);
              }
            },
            (error: HttpErrorResponse) => {
              console.error('Error loading folder for export:', error);
              reject(`Error loading step ${step.nom}: ${error.message}`);
            }
          );
        }
      });
      loadPromises.push(promise);
    });

    Promise.allSettled(loadPromises).then(results => {
      clearTimeout(startLoader);
      this.fonctionsService.loading_screen(false);

      const failedLoads = results.filter(result => result.status === 'rejected');
      if (failedLoads.length > 0) {
        console.error('Some loads failed:', failedLoads);
        this.fonctionsService.launch_toast(false);
        return;
      }

      this.download([], stepFolders, `All_Documents_${this.utilService.currentTir?.nom || 'Export'}`, this.utilService.currentTir.workflowInstance.workflowInstanceId);
    }).catch(error => {
      clearTimeout(startLoader);
      this.fonctionsService.loading_screen(false);
      console.error('Error loading steps:', error);
      this.fonctionsService.launch_toast(false);
    });
  }

  public addFilesToStep(stepId: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    const folder = this.getFolderForStep(stepId);
    if (folder) {
      this.currentFolder.set(folder);
      this.currentStepId.set(stepId);
      this.showFileUploadModal();
    }
  }

  loadFolder(step: WorkflowEtapeInstance) {
    if (step == null || step.workflowEtapeInstanceId == null || step === undefined) return;

    if (this.stepFolders().has(step.workflowEtapeInstanceId)) {
      return;
    }

    var startLoader = setTimeout(() => {
      this.fonctionsService.loading_screen(true);
    }, 300);
    this.http.get<Dossier>('WorkflowSteps/' + step.workflowEtapeInstanceId + '/Folder').subscribe(folder => {
      this.stepFolders.update(m => new Map(m).set(step.workflowEtapeInstanceId, folder));
      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
      this.cdRef.detectChanges();
    }, (error: HttpErrorResponse) => {
      if (error.status === 401) {
        this.translate.get('GLOBAL.NON_AUTORISE').subscribe(trad => {
          this.fonctionsService.launch_toast(false, false, trad)
          this.fonctionsService.loading_screen(false);
        });
      }
      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
    });
  }

  formatFileSize(sizeInBytes: number): string {
    if (!sizeInBytes) return '0Mo';
    const sizeInMB = sizeInBytes / (1024 * 1024);
    return Math.round(sizeInMB) + 'Mo';
  }

  formatFileDate(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

 public downloadFile(file: Fichier, stepId: number) {
    this.downloadElements([file], [], file.nom, stepId);
  }

  public exportFolder(folder: Dossier, stepId: number) {
    this.downloadElements([], [folder], folder.nom, stepId);
  }

    private downloadElements(files: Fichier[], folders: Dossier[], archiveName: string, stepId?: number) {
    if (files.length === 1 && folders.length === 0) {
      if (stepId === undefined || stepId === null) {
        console.log('Step ID is required');
        return;
      }
      
      this.openFile(files[0], stepId);

    } else {

      if ((stepId === undefined || stepId === null) && folders.length === 0)  {
        console.log('Step ID is required');
        return;
      }

      let startLoader = setTimeout(() => {
        this.fonctionsService.loading_screen(true);
      }, 300);
     
      let body = new FileFolder();
      body.dossiers = folders;
      body.fichiers = files;
      let fileCategory = this.documentStorageService.getCurrentFileCategory(this.apiEndPoint());
        clearTimeout(startLoader);
        this.fonctionsService.loading_screen(false);
       {
          let startLoader = setTimeout(() => {
            this.modalDownload.show();
          }, 300);
          this.downloadInProgress.set(true);

          this.documentStorageService.downloadFiles(body, stepId, this.tirId(), fileCategory).subscribe((result: any) => {
            const blob = new Blob([result], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            var a = document.createElement('a');
            a.href = url;
            a.download = archiveName + ".zip";
            document.body.appendChild(a);
            a.click();

            this.modalDownload.hide();
            this.downloadInProgress.set(false);
            clearTimeout(startLoader);
            setTimeout(() => {
              a.remove();
            }, 100);
          }, () => {
            this.modalDownload.hide();
            this.downloadInProgress.set(false);
            clearTimeout(startLoader);
          });
        
      }
    }
  }

  public downloadSubFolder(subFolder: Dossier) {
    this.downloadElements([], [subFolder], subFolder.nom);
  }

  public addFolderToStep(stepId: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    const folder = this.getFolderForStep(stepId);
    if (folder) {
      this.currentFolder.set(folder);
      this.currentStepId.set(stepId);
      this.modalFormDossier.show();
    }
  }

  rename(e: any) {
    this.renameNameError.set(false);
    this.renameNameRequired.set(false);
    this.isRenamingError.set(true);
    this.newName.set(this.newName().split(".")[0]);
    if (!this.validateFileOrFolderName(this.newName(), 'renameNameError', 'renameNameRequired'))
      return false;
    this.fonctionsService.loading_screen(true);
    const stepId = this.currentStepId();
    if (stepId == null) {
      this.fonctionsService.loading_screen(false);
      return false;
    }
    let request = this.currentRenamingFile() != null ?
      this.documentStorageService.renameFile(this.apiEndPoint(), stepId, this.currentRenamingFile()!.fichierId, this.tirId(), this.newName()) :
      this.documentStorageService.renameFolder(this.apiEndPoint(), stepId, this.currentRenamingFolder()!.dossierId, this.newName());

    request.subscribe((res: Dossier) => {
      this.refreshCurrentFolder(res);
      this.updateStepFolders(res);
      this.modalRenommer.hide();
      this.newName.set("");
      this.clearRenameState();
      this.currentStepId.set(null);
      this.fonctionsService.loading_screen(false);
    }, () => {
      this.documentStorageService.getStepFolder(this.apiEndPoint(), stepId).subscribe((res: Dossier) => {
        this.refreshCurrentFolder(res);
        this.updateStepFolders(res);
      });
      this.modalRenommer.hide();
      this.clearRenameState();
      this.currentStepId.set(null);
      this.fonctionsService.loading_screen(false);
      this.modalErreurSynchro.show();
    });
  }

  public renameFile(file: Fichier, stepId?: number) {
    this.currentRenamingFile.set(file);
    this.currentRenamingFolder.set(null);
    this.currentStepId.set(stepId || null);
    this.newName.set(file.nom);
    this.modalRenommer.show();
    this.newName.set(file.nom.split(".")[0]);
  }

  public renameFolder(folder: Dossier, stepId?: number) {
    this.currentRenamingFile.set(null);
    this.currentRenamingFolder.set(folder);
    this.currentStepId.set(stepId || null);
    this.newName.set(folder.nom);
    this.modalRenommer.show();
  }

  public deleteFile(file: Fichier, stepId?: number) {
    this.currentDeletingFile.set(file);
    this.currentDeletingFolder.set(null);
    this.currentStepId.set(stepId || null);
    this.modalConfirmSuppr.show();
  }

  public deleteFolder(folder: Dossier, stepId?: number) {
    this.currentDeletingFile.set(null);
    this.currentDeletingFolder.set(folder);
    this.currentStepId.set(stepId || null);
    this.modalConfirmSuppr.show();
  }

  public deleteSubFolder(subFolder: Dossier, stepId?: number) {
    this.currentDeletingFile.set(null);
    this.currentDeletingFolder.set(subFolder);
    this.currentStepId.set(stepId || null);
    this.modalConfirmSuppr.show();
  }

  deleteItems() {
    let requests = [];
    this.fonctionsService.loading_screen(true);
    
    
    if (this.currentDeletingFolder()) {
      requests.push(this.http.post(this.apiEndPoint() + "/" + this.currentStepId() + "/Folders/Delete", [this.currentDeletingFolder()]));
    }
    if (this.currentDeletingFile()) {
      requests.push(this.http.post(this.apiEndPoint() + "/" + this.currentStepId() + "/Files/Delete/" + this.tirId(), [this.currentDeletingFile()]));
    }

    if (requests.length === 0) {
      this.fonctionsService.loading_screen(false);
      return;
    }

    forkJoin(requests).subscribe((res: any) => {
      this.refreshCurrentFolder(res[res.length - 1]);
      this.updateStepFolders(res[res.length - 1]);
      this.modalConfirmSuppr.hide();
      this.clearDeleteState();
      this.currentStepId.set(null);
      this.fonctionsService.loading_screen(false);
      this.fonctionsService.launch_toast(true, false);
    }, (err: any) => {
      this.modalConfirmSuppr.hide();
      this.clearDeleteState();
      this.currentStepId.set(null);
      this.fonctionsService.loading_screen(false);
      this.fonctionsService.launch_toast(false);
    });
  }

  hideModalConfirmUpload() {
    this.modalConfirmUpload.hide();
    this.files.set([]);
    this.selectedFiles.set(new Set());
    this.fileDestinations.set([]);
    this.totalSize.set(0);
    this.warningDuplicateFile.set(false);
    this.selectedFolderId.set(null);
    this.isDragOver.set(false);
    
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = null;
    }
  }

  removeDeadReferences() {
    let fileFolder = new FileFolder();
    fileFolder.fichiers = [];
    fileFolder.dossiers = [];
    fileFolder.id = this.currentStepId();
    fileFolder.apiEndPoint = this.apiEndPoint();
    
    let startLoader = setTimeout(() => {
      this.fonctionsService.loading_screen(true);
    }, 300);
    this.http.post<FileFolder, Dossier>('Files/DeleteNonExistent', fileFolder).subscribe(result => {
      if (result) {
        this.fonctionsService.launch_toast(true, true);
        this.refreshCurrentFolder(result);
      }
      clearTimeout(startLoader);
      this.fonctionsService.loading_screen(false);
      this.modalFichiersSupprimes.hide();
    }, () => {
      clearTimeout(startLoader);
      this.fonctionsService.loading_screen(false);
      this.fonctionsService.launch_toast(false, true);
      this.modalFichiersSupprimes.hide();
    });
  }

  private reloadStepFolder(stepId: number) {
    this.http.get<Dossier>('WorkflowSteps/' + stepId + '/Folder').subscribe(folder => {
      this.stepFolders.update(m => new Map(m).set(stepId, folder));
    }, (error: HttpErrorResponse) => {
      console.error('Error reloading step folder:', error);
    });
  }

  private updateStepFolders(newRootFolder: Dossier) {
    if (this.currentStepId()) {
      this.reloadStepFolder(this.currentStepId());
    } else {
      this.stepFolders().forEach((folder, stepId) => {
        if (folder.dossierId === newRootFolder.dossierId) {
          this.reloadStepFolder(stepId);
        }
      });
    }
  }

  private clearRenameState() {
    this.currentRenamingFile.set(null);
    this.currentRenamingFolder.set(null);
  }

  private clearDeleteState() {
    this.currentDeletingFile.set(null);
    this.currentDeletingFolder.set(null);
  }

  filesFromInput(e: any) {
    const fileList = e.srcElement.files;
    if (fileList && fileList.length > 0) {
      this.processFiles(fileList);
    }
  }

  protected getFileExtension(fileName: string) {
    return fileName.slice((fileName.lastIndexOf(".") - 1 >>> 0) + 2);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
    
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      this.processFiles(droppedFiles);
    }
  }

  onStepDragEnter(event: DragEvent, stepId: number): void {
    event.preventDefault();
    event.stopPropagation();
    
    const currentCounter = this.stepDragCounter().get(stepId) || 0;
    this.stepDragCounter.update(m => new Map(m).set(stepId, currentCounter + 1));
    
    if (currentCounter === 0) {
      this.stepDragOver.update(m => new Map(m).set(stepId, true));
    }
  }

  onStepDragOver(event: DragEvent, stepId: number): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onStepDragLeave(event: DragEvent, stepId: number): void {
    event.preventDefault();
    event.stopPropagation();

    const currentCounter = this.stepDragCounter().get(stepId) || 0;
    const newCounter = Math.max(0, currentCounter - 1);
    this.stepDragCounter.update(m => new Map(m).set(stepId, newCounter));

    if (newCounter === 0) {
      this.stepDragOver.update(m => new Map(m).set(stepId, false));
    }
  }

  onStepDrop(event: DragEvent, stepId: number): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.stepDragOver.update(m => new Map(m).set(stepId, false));
    this.stepDragCounter.update(m => new Map(m).set(stepId, 0));

    const target = event.target as Element;

    const isInteractiveElement = target.closest('button, input, select, .checkbox-container, .file-actions, .btn-actions');
    if (isInteractiveElement) {
      return;
    }


    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      this.uploadFilesToStep(droppedFiles, stepId);
    }
  }

  onSubFolderDragEnter(event: DragEvent, folderId: number): void {
    event.preventDefault();
    event.stopPropagation();

    const currentCounter = this.subFolderDragCounter().get(folderId) || 0;
    this.subFolderDragCounter.update(m => new Map(m).set(folderId, currentCounter + 1));

    if (currentCounter === 0) {
      this.subFolderDragOver.update(m => new Map(m).set(folderId, true));
    }
  }

  onSubFolderDragOver(event: DragEvent, folderId: number): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onSubFolderDragLeave(event: DragEvent, folderId: number): void {
    event.preventDefault();
    event.stopPropagation();

    const currentCounter = this.subFolderDragCounter().get(folderId) || 0;
    const newCounter = Math.max(0, currentCounter - 1);
    this.subFolderDragCounter.update(m => new Map(m).set(folderId, newCounter));

    if (newCounter === 0) {
      this.subFolderDragOver.update(m => new Map(m).set(folderId, false));
    }
  }

  onSubFolderDrop(event: DragEvent, stepId: number, folderId: number): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.subFolderDragOver.update(m => new Map(m).set(folderId, false));
    this.subFolderDragCounter.update(m => new Map(m).set(folderId, 0));

    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      this.uploadFilesToSubFolder(droppedFiles, stepId, folderId);
    }
  }

  private uploadFilesToStep(fileList: FileList, stepId: number): void {
    this.uploadFilesToFolder(fileList, stepId);
  }

  private uploadFilesToSubFolder(fileList: FileList, stepId: number, subFolderId: number): void {
    this.uploadFilesToFolder(fileList, stepId, subFolderId);
  }

  private uploadFilesToFolder(fileList: FileList, stepId: number, folderId?: number): void {
    const folder = this.getFolderForStep(stepId);
    if (!folder) {
      this.fonctionsService.launch_toast(false, false, 'Unable to access step folder');
      return;
    }

    this.currentFolder.set(folder);
    this.currentStepId.set(stepId);
    this.files.set([]);
    this.processFiles(fileList);

    if (this.files().length > 0) {
      this.selectedFiles.set(new Set());
      const newDests: (number | null)[] = [];
      for (let i = 0; i < this.files().length; i++) {
        this.selectedFiles.update(s => new Set([...s, i]));
        newDests[i] = null;
      }
      this.fileDestinations.set(newDests);

      this.selectedFolderId.set(folderId || null);

      this.uploadSelectedFiles();
    } else {
      this.currentStepId.set(null);
    }
  }

  private extractFolderIdFromElement(element: Element): number | null {
    const folderCard = element.closest('.sub-folder-card[data-folder-id]');
    if (folderCard) {
      const folderIdStr = folderCard.getAttribute('data-folder-id');
      if (folderIdStr) {
        const folderId = parseInt(folderIdStr, 10);
        return isNaN(folderId) ? null : folderId;
      }
    }
    return null;
  }

  private processFiles(fileList: FileList): void {
    const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2 GB
    const filesArray = Array.from(fileList);
    const oversizedFiles: File[] = [];
    const validFiles: File[] = [];
    
    filesArray.forEach(file => {
      if (file.size === 0) {
        return; // Skip empty files
      } else if (file.size >= MAX_FILE_SIZE) {
        oversizedFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });
    
    // Show error message for oversized files
    if (oversizedFiles.length > 0) {
      const maxSizeMB = Math.round(MAX_FILE_SIZE / (1024 * 1024));
      
      if (oversizedFiles.length === 1) {
        this.translate.get('FILE_EXPLORER.FILE_TOO_LARGE_SINGLE', { 
          fileName: oversizedFiles[0].name, 
          maxSize: maxSizeMB 
        }).subscribe(message => {
          this.fonctionsService.launch_toast(false, false, message);
        });
      } else {
        const fileNames = oversizedFiles.map(f => f.name).join(', ');
        this.translate.get('FILE_EXPLORER.FILE_TOO_LARGE_MULTIPLE', { 
          count: oversizedFiles.length, 
          maxSize: maxSizeMB, 
          fileNames: fileNames 
        }).subscribe(message => {
          this.fonctionsService.launch_toast(false, false, message);
        });
      }
    }
    
    if (validFiles.length === 0) {
      if (oversizedFiles.length === 0) {
        this.translate.get('FILE_EXPLORER.NO_VALID_FILES').subscribe(message => {
          this.fonctionsService.launch_toast(false, false, message);
        });
      }
      return;
    }
    
    const existingFileNames = this.files().map(f => f.name.toLowerCase());
    const uniqueValidFiles = validFiles.filter(file => {
      const isDuplicate = existingFileNames.includes(file.name.toLowerCase());
      if (isDuplicate) {
        this.translate.get('FILE_EXPLORER.DUPLICATE_FILE_SKIPPED', { fileName: file.name }).subscribe(message => {
          this.fonctionsService.launch_toast(false, false, message);
        });
      }
      return !isDuplicate;
    });
    
    if (uniqueValidFiles.length === 0) {
      this.translate.get('FILE_EXPLORER.ALL_FILES_DUPLICATES').subscribe(message => {
        this.fonctionsService.launch_toast(false, false, message);
      });
      return;
    } 
    const previousFileCount = this.files().length;
    this.files.update(f => [...f, ...uniqueValidFiles]);

    for (let i = previousFileCount; i < this.files().length; i++) {
      this.selectedFiles.update(s => new Set([...s, i]));
    }
    const newDestinations = new Array(uniqueValidFiles.length).fill(null);
    this.fileDestinations.update(d => [...d, ...newDestinations]);
    
    this.calculateTotalSize();
    this.checkDuplicateFiles();
  }

  isFileSelected(index: number): boolean {
    return this.selectedFiles().has(index);
  }

  toggleFileSelection(index: number, event: Event): void {
    event.stopPropagation();
    
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.selectedFiles.update(s => new Set([...s, index]));
      this.fileDestinations.update(d => { d[index] = null; return [...d]; });
    } else {
      this.selectedFiles.update(s => { s.delete(index); return new Set(s); });
      this.fileDestinations.update(d => { d[index] = null; return [...d]; });
    }
  }

  areAllFilesSelected(): boolean {
    return this.files().length > 0 && this.selectedFiles().size === this.files().length;
  }

  areSomeFilesSelected(): boolean {
    return this.selectedFiles().size > 0 && this.selectedFiles().size < this.files().length;
  }

  toggleSelectAllFiles(event: Event): void {
    event.stopPropagation();
    
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      const allSelected = new Set<number>();
      const allDests: (number | null)[] = [];
      for (let i = 0; i < this.files().length; i++) {
        allSelected.add(i);
        allDests[i] = null;
      }
      this.selectedFiles.set(allSelected);
      this.fileDestinations.set(allDests);
    } else {
      this.selectedFiles.set(new Set());
      const clearedDests = this.fileDestinations().map(() => null);
      this.fileDestinations.set(clearedDests);
    }
  }

  getSelectedFiles(): number[] {
    return Array.from(this.selectedFiles());
  }

  applyFolderToSelected(): void {
    this.getSelectedFiles().forEach(index => {
      this.fileDestinations.update(d => { d[index] = null; return [...d]; });
    });
  }

 uploadSelectedFiles(): void {
  if (this.files().length === 0) {
    this.fonctionsService.launch_toast(false);
    return;
  }

  this.modalConfirmUpload.hide();
  this.modalUpload.show();

  const filesByFolder = new Map<number | null, File[]>();
  
  this.files().forEach((file, index) => {
    let destination: number | null;
    
    if (this.selectedFiles().has(index)) {
      destination = this.selectedFolderId();
    } else {
      destination = this.fileDestinations()[index];
    }
    
    if (!filesByFolder.has(destination)) {
      filesByFolder.set(destination, []);
    }
    filesByFolder.get(destination)!.push(file);
  });

  const uploadPromises: Promise<any>[] = [];
  
  filesByFolder.forEach((files, folderId) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append(file.name, file);
    });
    
    const targetFolderId = folderId || this.currentFolder()!.dossierId;
    formData.append("dossierId", targetFolderId.toString());
    
    const uploadPromise = new Promise((resolve, reject) => {
      this.http.request('POST', this.apiEndPoint() + '/' + this.currentStepId() + '/Files/Upload', formData).subscribe(
        event => {
          if (event.type === HttpEventType.UploadProgress) {
            this.barProgress.set(Math.round(event.loaded / event.total * 100));
            this.progress.set(this.barProgress().toString() + '%');
            this.cdRef.detectChanges();
          }
          if (event.type === HttpEventType.Response) {
            resolve(event);
          }
        },
        error => reject(error)
      );
    });
    
    uploadPromises.push(uploadPromise);
  });

  Promise.allSettled(uploadPromises).then(results => {
    let successCount = 0;
    let errorCount = 0;
    
    results.forEach(result => {
      if (result.status === 'fulfilled') {
        successCount++;
        if (result.value && result.value.body) {
          this.refreshCurrentFolder(result.value.body);
          if (this.currentStepId()) {
            this.stepFolders.update(m => new Map(m).set(this.currentStepId()!, result.value.body));
          }
        }
      } else {
        errorCount++;
      }
    });
    
    this.modalUpload.hide();
    
    this.files.set([]);
    this.selectedFiles.set(new Set());
    this.fileDestinations.set([]);
    this.totalSize.set(0);
    this.warningDuplicateFile.set(false);
    this.selectedFolderId.set(null);
    this.isDragOver.set(false);
    
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = null;
    }
    this.currentStepId.set(null);
    
    if (errorCount === 0) {
      this.fonctionsService.launch_toast(true, false);
    } else {
      this.fonctionsService.launch_toast(false, false, `Upload completed with ${errorCount} error(s)`);
    }
  }).catch(error => {
    this.modalUpload.hide();
    this.currentStepId.set(null);
    this.fonctionsService.launch_toast(false);
  });
}

  private calculateTotalSize(): void {
    this.totalSize.set(this.files().reduce((sum, f) => sum + f.size, 0));
  }

  private checkDuplicateFiles(): void {
    this.warningDuplicateFile.set(false);
    
    if (!this.currentFolder() || !this.currentFolder()?.fichiers) {
      return;
    }

    for (let i = 0; i < this.files().length; i++) {
      let name = this.files()[i].name;
      let indexPoint = this.files()[i].name.lastIndexOf('.');
      if (indexPoint != -1) {
        name = this.files()[i].name.substr(0, indexPoint) + this.files()[i].name.substr(indexPoint).toLowerCase();
      }

      if (this.currentFolder()!.fichiers.find(p => p.nom == name)) {
        this.warningDuplicateFile.set(true);
        break;
      }
    }
  }

  removeFile(index: number): void {
    this.files.update(f => { f.splice(index, 1); return [...f]; });
    
    const newSelectedFiles = new Set<number>();
    this.selectedFiles().forEach(selectedIndex => {
      if (selectedIndex < index) {
        newSelectedFiles.add(selectedIndex);
      } else if (selectedIndex > index) {
        newSelectedFiles.add(selectedIndex - 1);
      }
    });
    this.selectedFiles.set(newSelectedFiles);
    
    this.fileDestinations.update(d => { d.splice(index, 1); return [...d]; });
    
    this.calculateTotalSize();
    this.checkDuplicateFiles();
  }

  getAvailableFolders(): Dossier[] {
    if (!this.currentFolder()) return [];
    return this.currentFolder()!.sousDossiers || [];
  }

  showFileUploadModal(): void {
    this.files.set([]);
    this.selectedFiles.set(new Set());
    this.fileDestinations.set([]);
    this.totalSize.set(0);
    this.warningDuplicateFile.set(false);
    this.selectedFolderId.set(null);
    this.modalConfirmUpload.show();
  }

  triggerFileInput(): void {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  public isItemSelected(type: 'file' | 'folder', itemId: number, stepId: number): boolean {
    const stepSelections = this.selectedItems().get(stepId);
    if (!stepSelections) return false;
    const key = `${type}:${itemId}`;
    return stepSelections.has(key);
  }

  public toggleItemSelection(type: 'file' | 'folder', itemId: number, stepId: number, event: Event): void {
    event.stopPropagation();
    
    const target = event.target as HTMLInputElement;
    const key = `${type}:${itemId}`;
    
    if (!this.selectedItems().has(stepId)) {
      this.selectedItems.update(m => new Map(m).set(stepId, new Set()));
    }
    
    const stepSelections = this.selectedItems().get(stepId)!;
    
    if (target.checked) {
      stepSelections.add(key);
      
      if (type === 'folder') {
        this.selectAllFilesInFolder(itemId, stepId, stepSelections);
      }
    } else {
      stepSelections.delete(key);
      
      if (type === 'folder') {
        this.deselectAllFilesInFolder(itemId, stepId, stepSelections);
      } else if (type === 'file') {
        this.checkAndDeselectParentFolder(itemId, stepId, stepSelections);
      }
    }
  }

  private checkAndDeselectParentFolder(fileId: number, stepId: number, stepSelections: Set<string>): void {
    const parentFolder = this.findParentFolderOfFile(fileId, stepId);
    if (!parentFolder) return;
    
    const parentKey = `folder:${parentFolder.dossierId}`;
    
    if (stepSelections.has(parentKey)) {
      stepSelections.delete(parentKey);
    }
  }

  private findParentFolderOfFile(fileId: number, stepId: number): Dossier | null {
    const rootFolder = this.getFolderForStep(stepId);
    if (!rootFolder) return null;
    
    return this.searchParentFolderRecursively(rootFolder, fileId);
  }

  private searchParentFolderRecursively(folder: Dossier, targetFileId: number): Dossier | null {
    if (folder.fichiers && folder.fichiers.some(file => file.fichierId === targetFileId)) {
      return folder;
    }
    
    if (folder.sousDossiers) {
      for (const subFolder of folder.sousDossiers) {
        const found = this.searchParentFolderRecursively(subFolder, targetFileId);
        if (found) return found;
      }
    }
    
    return null;
  }

  private selectAllFilesInFolder(folderId: number, stepId: number, stepSelections: Set<string>): void {
    const folder = this.findFolderById(folderId, stepId);
    if (!folder) return;
    if (folder.fichiers) {
      folder.fichiers.forEach(file => {
        stepSelections.add(`file:${file.fichierId}`);
      });
    }
    
    if (folder.sousDossiers) {
      folder.sousDossiers.forEach(subFolder => {
        this.selectAllFilesInFolder(subFolder.dossierId, stepId, stepSelections);
      });
    }
  }

  private deselectAllFilesInFolder(folderId: number, stepId: number, stepSelections: Set<string>): void {
    const folder = this.findFolderById(folderId, stepId);
    if (!folder) return;
    
    if (folder.fichiers) {
      folder.fichiers.forEach(file => {
        stepSelections.delete(`file:${file.fichierId}`);
      });
    }
    
    if (folder.sousDossiers) {
      folder.sousDossiers.forEach(subFolder => {
        this.deselectAllFilesInFolder(subFolder.dossierId, stepId, stepSelections);
      });
    }
  }

  private findFolderById(folderId: number, stepId: number): Dossier | null {
    const rootFolder = this.getFolderForStep(stepId);
    if (!rootFolder) return null;
    
    return this.searchFolderRecursively(rootFolder, folderId);
  }

  private searchFolderRecursively(folder: Dossier, targetId: number): Dossier | null {
    if (folder.dossierId === targetId) {
      return folder;
    }
    
    if (folder.sousDossiers) {
      for (const subFolder of folder.sousDossiers) {
        const found = this.searchFolderRecursively(subFolder, targetId);
        if (found) return found;
      }
    }
    
    return null;
  }

  public isAllSelected(stepId: number): boolean {
    const folder = this.getFolderForStep(stepId);
    if (!folder) return false;
    
    const totalItems = this.getTotalSelectableItems(folder);
    const selectedCount = this.getSelectedItemsCount(stepId);
    
    return totalItems > 0 && selectedCount === totalItems;
  }

  public isIndeterminate(stepId: number): boolean {
    const folder = this.getFolderForStep(stepId);
    if (!folder) return false;
    
    const totalItems = this.getTotalSelectableItems(folder);
    const selectedCount = this.getSelectedItemsCount(stepId);
    
    return selectedCount > 0 && selectedCount < totalItems;
  }

  public toggleSelectAll(stepId: number, event: Event): void {
    event.stopPropagation();
    
    const target = event.target as HTMLInputElement;
    const folder = this.getFolderForStep(stepId);
    if (!folder) return;
    
    if (!this.selectedItems().has(stepId)) {
      this.selectedItems.update(m => new Map(m).set(stepId, new Set()));
    }
    
    const stepSelections = this.selectedItems().get(stepId)!;
    
    if (target.checked) {
      this.selectAllItems(folder, stepSelections);
    } else {
      stepSelections.clear();
    }
  }

  public hasSelectedItems(stepId: number): boolean {
    const stepSelections = this.selectedItems().get(stepId);
    return stepSelections ? stepSelections.size > 0 : false;
  }

  private getTotalSelectableItems(folder: Dossier): number {
    let count = 0;
    
    if (folder.fichiers) {
      count += folder.fichiers.length;
    }
    
    if (folder.sousDossiers) {
      count += folder.sousDossiers.length;
      folder.sousDossiers.forEach(subFolder => {
        if (subFolder.fichiers) {
          count += subFolder.fichiers.length;
        }
      });
    }
    
    return count;
  }

  private getSelectedItemsCount(stepId: number): number {
    const stepSelections = this.selectedItems().get(stepId);
    return stepSelections ? stepSelections.size : 0;
  }

  private selectAllItems(folder: Dossier, selections: Set<string>): void {
    if (folder.fichiers) {
      folder.fichiers.forEach(file => {
        selections.add(`file:${file.fichierId}`);
      });
    }
    
    if (folder.sousDossiers) {
      folder.sousDossiers.forEach(subFolder => {
        selections.add(`folder:${subFolder.dossierId}`);
        this.selectAllItems(subFolder, selections);
      });
    }
  }

  private buildSelectedItemsArrays(stepId: number, folder: Dossier): { selectedFiles: Fichier[], selectedFolders: Dossier[] } {
    const stepSelections = this.selectedItems().get(stepId);
    const selectedFiles: Fichier[] = [];
    const selectedFolders: Dossier[] = [];
    
    if (!stepSelections) {
      return { selectedFiles, selectedFolders };
    }
    this.collectSelectedFoldersAndFiles(folder, stepSelections, selectedFiles, selectedFolders, true);
    
    return { selectedFiles, selectedFolders };
  }

  private collectSelectedFoldersAndFiles(
    folder: Dossier, 
    stepSelections: Set<string>, 
    selectedFiles: Fichier[], 
    selectedFolders: Dossier[],
    isRootLevel = false
  ): void {
    const folderKey = `folder:${folder.dossierId}`;
    const isFolderSelected = stepSelections.has(folderKey);
    
    if (isFolderSelected) {
      selectedFolders.push(folder);
    } else {
      const selectedFilesInFolder: Fichier[] = [];
      if (folder.fichiers) {
        folder.fichiers.forEach(file => {
          if (stepSelections.has(`file:${file.fichierId}`)) {
            if (isRootLevel) {
              selectedFiles.push(file);
            } else {
              selectedFilesInFolder.push(file);
            }
          }
        });
      }
      if (!isRootLevel && selectedFilesInFolder.length > 0) {
        const partialFolder = new Dossier();
        partialFolder.dossierId = folder.dossierId;
        partialFolder.nom = folder.nom;
        partialFolder.dossierParentId = folder.dossierParentId;
        partialFolder.siteId = folder.siteId;
        partialFolder.projetId = folder.projetId;
        partialFolder.dossierParent = folder.dossierParent;
        partialFolder.site = folder.site;
        partialFolder.projet = folder.projet;
        partialFolder.isSelected = folder.isSelected;
        partialFolder.fichiers = selectedFilesInFolder;
        partialFolder.sousDossiers = [];
        selectedFolders.push(partialFolder);
      }
      if (folder.sousDossiers) {
        folder.sousDossiers.forEach(subFolder => {
          this.collectSelectedFoldersAndFiles(subFolder, stepSelections, selectedFiles, selectedFolders, false);
        });
      }
    }
  }

    private download(selectedFiles: Fichier[], selectedFolders: Dossier[], folderName = 'documents', stepId: number): void {
    if (selectedFiles.length == 1 && selectedFolders.length == 0) {
      this.openFile(selectedFiles[0], stepId);
    } else {
      let startLoader = setTimeout(() => {
        this.fonctionsService.loading_screen(true);
      }, 300);

      let body = new FileFolder();
      body.dossiers = selectedFolders;
      body.fichiers = selectedFiles;      
      let fileCategory = this.documentStorageService.getCurrentFileCategory(this.apiEndPoint());
      clearTimeout(startLoader);
      this.fonctionsService.loading_screen(false);
      this.modalDownload.show();
      this.documentStorageService.downloadFiles(body, stepId, this.tirId(), fileCategory).subscribe((result: any) => {
        const blob = new Blob([result], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            var a = document.createElement('a');
            a.href = url;
            a.download = folderName + ".zip";
            document.body.appendChild(a);
            a.click();

            this.modalDownload.hide();
            clearTimeout(startLoader);
            setTimeout(() => {
              a.remove();
            }, 100);
          }, () => {
            this.modalDownload.hide();
            clearTimeout(startLoader);
          });
        }     
  }

  private openFile(file: Fichier, stepId: number): void {
    this.fonctionsService.loading_screen(true);
    const fileFolder = new FileFolder();
    fileFolder.fichiers = [file];

    if(file.storedInS3)
    {
      const  s3key = `${file.siteId}/${this.tirId()}/workflow/${stepId}/${file.nom}`;      
      this.documentStorageService.getDownloadUrl(s3key,file.fichierId, file.siteId, DirectoryType.USERFILES).subscribe({
        next: (result) => {
            if (result && result.downloadUrl) {
              window.open(result.downloadUrl, '_blank');
              this.fonctionsService.loading_screen(false);
            }
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error);
        },
      });
    } else {
      this.documentStorageService.checkFilesExist(fileFolder).subscribe({
        next: (result) => {
          if (result) {
            const query = this.buildFileQuery(file);
            this.downloadSingleFile(query, file.nom);
          } else {
            this.handleFileNotFound();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error);
        },
      });
    }
  }

  private buildFileQuery(file: Fichier): string {
    return `Files/${file.fichierId}/Open/${file.siteId}?fileType=${DirectoryType.USERFILES}`;
  }

  private downloadSingleFile(query: string, fileName: string) {
    this.http.downloadFile(query).subscribe({
      next: (res) => {
        if (res) {
          this.triggerFileDownload(res.body, fileName);
        } else {
          this.handleDownloadError();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.handleError(error);
      },
    });
  }

  private triggerFileDownload(fileData: BlobPart, fileName: string) {
    const blob = new Blob([fileData], { type: 'base64' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      a.remove();
      this.fonctionsService.loading_screen(false);
    }, 1000);
  }

  private handleFileNotFound() {
    this.fonctionsService.launch_toast(false, false, 'File not found');
    this.fonctionsService.loading_screen(false);
  }

  private handleDownloadError() {
    this.fonctionsService.launch_toast(false, false, 'Error during file download');
    this.fonctionsService.loading_screen(false);
  }

  private handleError(error: HttpErrorResponse) {
    console.error('Error:', error);
    this.fonctionsService.launch_toast(false, false, 'An error occurred');
    this.fonctionsService.loading_screen(false);
  }

  protected openFileInNewWindow(f: Fichier, e:any, stepId?: number){
      e.stopPropagation()

      if(f.storedInS3){
        if (stepId === undefined || stepId === null) {
        console.log('Step ID is required');
          return;
        }
      const s3key = `${f.siteId}/${this.tirId()}/workflow/${stepId}/${f.nom}`;
        this.documentStorageService.getDownloadUrl(s3key, f.fichierId, f.siteId, DirectoryType.USERFILES).subscribe({
          next: (result) => {   
            if (result && result.downloadUrl) {
              window.open(result.downloadUrl, '_blank');
            }
          },
          error: (error: HttpErrorResponse) => {
            if(error.status === 404){
              this.translate.get('GLOBAL.NO_FILE_FOUND').subscribe(trad => {
                this.fonctionsService.launch_toast(false, false, trad);
              });
             }
          },
        }); 
      }else{
      const url = 'Files/' + f.fichierId + '/Open/' + f.siteId + '/?fileType=2';
      this.documentStorageService.openFileInNewWindow(url).subscribe(res => {
          const fileURL = URL.createObjectURL(res);
          window.open(fileURL, '_blank');
        },(error:HttpErrorResponse)=>{
             if(error.status === 404){
              this.translate.get('GLOBAL.NO_FILE_FOUND').subscribe(trad => {
                this.fonctionsService.launch_toast(false, false, trad);
              });
             }
        });
      }
    }


      protected checkIfFileCanBePreview(f: Fichier){
    const filesAllowedToPreview = ['png', 'jpg', 'jpeg', 'pdf'];
    const fileExtension = this.getFileExtension(f.nom).toLowerCase(); 
    return filesAllowedToPreview.includes(fileExtension);
  }
}

class FileFolder {
  fichiers: Fichier[];
  dossiers: Dossier[];
  id: number;
  apiEndPoint: string;
}
